import { ATTACH_SOURCES, type AttachSource } from './article-addition'
import type { IssueSource } from './report-issue'

/**
 * What a person can type for an article added by hand, per source (CLI-625; Kevin, 2026-10-13:
 * each source has its own fields and that has to be clear). ONE catalogue drives the Library's
 * Add articles, the editor's By hand, the RTA's manual alert and the CSV template, so a source
 * asks for the same things on every screen, and every field here reaches the warehouse through
 * the attach (`attachArticleExtrasSchema`): the hit row, its full text, its snippets, or the
 * family's own metadata. The three attachable sources are the ones the data API can write; the
 * other three are typed into the issue or the alert alone, with the shape the renderer reads.
 */

export const MANUAL_FIELD_KEYS = [
  'url',
  'title',
  'outlet',
  'author',
  'handle',
  'publishDate',
  'language',
  'country',
  'description',
  'snippets',
  'content',
  'followers',
  'views',
  'likes',
  'reposts',
  'replies',
  'audience',
  'sender',
] as const
export type ManualFieldKey = (typeof MANUAL_FIELD_KEYS)[number]

/** How a field is typed in: one line, several, a date, a count, a list of lines, or a long text. */
export type ManualFieldKind = 'url' | 'text' | 'multiline' | 'date' | 'number' | 'list' | 'longtext'

export interface ManualField {
  key: ManualFieldKey
  label: string
  kind: ManualFieldKind
  placeholder?: string
  hint?: string
}

export interface ManualShape {
  /** One sentence under the source picker saying what this source is made of. */
  note: string
  /** Link and title first, always; the rest in the order the form shows them. */
  fields: ManualField[]
}

const KIND_OF: Record<ManualFieldKey, ManualFieldKind> = {
  url: 'url',
  title: 'text',
  outlet: 'text',
  author: 'text',
  handle: 'text',
  publishDate: 'date',
  language: 'text',
  country: 'text',
  description: 'multiline',
  snippets: 'list',
  content: 'longtext',
  followers: 'number',
  views: 'number',
  likes: 'number',
  reposts: 'number',
  replies: 'number',
  audience: 'number',
  sender: 'text',
}

const field = (
  key: ManualFieldKey,
  label: string,
  over: Partial<ManualField> = {},
): ManualField => ({
  key,
  label,
  kind: KIND_OF[key],
  ...over,
})

const language = field('language', 'Language', { placeholder: 'en' })
const snippets = field('snippets', 'Snippets', { hint: 'The lines a report quotes, in order.' })

export const MANUAL_SHAPES: Record<IssueSource, ManualShape> = {
  news: {
    note: 'An article: the outlet and byline, what shows under the headline, and the full text.',
    fields: [
      field('url', 'Link'),
      field('title', 'Headline'),
      field('outlet', 'Outlet'),
      field('author', 'Author'),
      field('publishDate', 'Published'),
      language,
      field('country', 'Country', { placeholder: 'US' }),
      field('description', 'Description', { hint: 'The line under the headline.' }),
      snippets,
      field('content', 'Full text'),
    ],
  },
  x: {
    note: 'A post on X: the account, its handle, and the counts X shows.',
    fields: [
      field('url', 'Post link'),
      field('title', 'Post text', { kind: 'multiline' }),
      field('outlet', 'Account name'),
      field('handle', 'Handle', { placeholder: '@name' }),
      field('publishDate', 'Posted'),
      language,
      field('followers', 'Followers'),
      field('views', 'Views'),
      field('likes', 'Likes'),
      field('reposts', 'Reposts'),
      field('replies', 'Replies'),
    ],
  },
  linkedin: {
    note: 'A LinkedIn post: the person or company, the post itself, and the counts the page shows.',
    fields: [
      field('url', 'Post link'),
      field('title', 'Post text', { kind: 'multiline' }),
      field('outlet', 'Person or company'),
      field('handle', 'Profile handle', {
        hint: 'The part after linkedin.com/in/ or linkedin.com/company/.',
      }),
      field('publishDate', 'Posted'),
      field('language', 'Language', { placeholder: 'en' }),
      field('followers', 'Followers', { kind: 'number' }),
      field('likes', 'Likes', { kind: 'number' }),
      field('replies', 'Comments', { kind: 'number' }),
    ],
  },
  broadcast: {
    note: 'A TV or radio segment: the station (required), the program, and the transcript line a report quotes. The clip lands under the broadcast searches the topic already runs.',
    fields: [
      field('url', 'Clip link'),
      field('title', 'Segment title'),
      field('outlet', 'Station'),
      field('author', 'Program'),
      field('publishDate', 'Aired'),
      field('description', 'Transcript excerpt', {
        kind: 'multiline',
        hint: 'The line a report quotes; name the topic’s term in it.',
      }),
      field('content', 'Transcript', { kind: 'longtext' }),
      field('audience', 'Reach', { kind: 'number' }),
    ],
  },
  podcast: {
    note: 'A podcast episode: the show, its host, a description and the transcript.',
    fields: [
      field('url', 'Episode link'),
      field('title', 'Episode title'),
      field('outlet', 'Podcast'),
      field('author', 'Host'),
      field('publishDate', 'Published'),
      language,
      field('description', 'Description'),
      snippets,
      field('content', 'Transcript'),
      field('audience', 'Audience estimate'),
    ],
  },
  newsletter: {
    note: 'A newsletter issue: which newsletter, the sender it comes from (what a newsletter topic matches on), and the line to quote.',
    fields: [
      field('url', 'Link'),
      field('title', 'Subject line'),
      field('outlet', 'Newsletter'),
      field('sender', 'Sender email', {
        placeholder: 'news@example.com',
        hint: 'Blank takes the first sender of a topic picked below.',
      }),
      field('author', 'Author'),
      field('publishDate', 'Sent'),
      field('description', 'Snippet', { kind: 'multiline' }),
      field('content', 'Body text', { kind: 'longtext' }),
      field('audience', 'Audience estimate', { kind: 'number' }),
    ],
  },
}

export const manualFields = (source: IssueSource): ManualField[] => MANUAL_SHAPES[source].fields

/** The CSV column a field is read from: the key, snake_cased. */
export const CSV_COLUMN: Record<ManualFieldKey, string> = Object.fromEntries(
  MANUAL_FIELD_KEYS.map((key) => [key, key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`)]),
) as Record<ManualFieldKey, string>

export const isAttachSource = (source: string): source is AttachSource =>
  (ATTACH_SOURCES as readonly string[]).includes(source)

const X_HOSTS = new Set(['x.com', 'twitter.com', 'mobile.twitter.com'])
const LINKEDIN_HOSTS = new Set(['linkedin.com', 'lnkd.in'])
const BROADCAST_HOSTS = new Set(['mms.tveyes.com', 'tveyes.com', 'app.tveyes.com'])
/** Newsletter platforms whose issue links a person pastes; matched on the host's last labels. */
const NEWSLETTER_HOST_SUFFIXES = [
  'substack.com',
  'beehiiv.com',
  'mailchi.mp',
  'campaign-archive.com',
  'buttondown.email',
  'ghost.io',
]
const PODCAST_HOSTS = new Set([
  'podcasts.apple.com',
  'open.spotify.com',
  'podchaser.com',
  'pod.link',
  'overcast.fm',
  'pocketcasts.com',
  'castbox.fm',
])

/**
 * Which family a URL belongs to when the person did not say: X, LinkedIn and TVEyes by their
 * hosts, a podcast by the hosts the directories live on, a newsletter by the platforms issues
 * are published on, and news for everything else — the safe default, since an unknown host is
 * far more likely a publication than a show. An explicit source always wins.
 */
const HOST_RE = /^[a-z][a-z0-9+.-]*:\/\/(?:[^@/?#]*@)?([^:/?#]+)/i

export function inferAttachSource(url: string): AttachSource {
  const host = HOST_RE.exec(url.trim())?.[1]
    ?.toLowerCase()
    .replace(/^www\./, '')
  if (!host) return 'news'
  if (X_HOSTS.has(host)) return 'x'
  if (LINKEDIN_HOSTS.has(host)) return 'linkedin'
  if (BROADCAST_HOSTS.has(host)) return 'broadcast'
  if (PODCAST_HOSTS.has(host)) return 'podcast'
  if (NEWSLETTER_HOST_SUFFIXES.some((suffix) => host === suffix || host.endsWith(`.${suffix}`)))
    return 'newsletter'
  return 'news'
}
